import styled from "styled-components";
import { NsTheme } from "../ns-eds-types";
import { useTheme } from "../contexts/useTheme";

export type MessageBarType = "informative" | "success" | "error" | "warning";

interface MessageBarProps extends React.PropsWithChildren {
  readonly messageBarType: MessageBarType;
}
export function MessageBar({ messageBarType, children }: MessageBarProps) {
  const { theme } = useTheme();
  const { $color, $colorSubtle } = getColorProps(theme.colors, messageBarType);

  return (
    <StyledMessageBar
      $color={$color}
      $colorSubtle={$colorSubtle}
      role={messageBarType === "error" ? "alert" : undefined}
    >
      <StyledIconSpan $color={$color} $colorSubtle={$colorSubtle}>
        <tractie-icon name={iconNameForType[messageBarType]} />
      </StyledIconSpan>
      <MessageSpan>{children}</MessageSpan>
    </StyledMessageBar>
  );
}

const iconNameForType: Record<MessageBarType, string> = {
  informative: "info-filled",
  success: "check-circle-filled",
  error: "error-filled",
  warning: "alert-filled",
};

function getColorProps(
  colors: NsTheme["colors"],
  messageBarType: MessageBarType,
): WithMessageBarColors {
  const colorMap: Record<MessageBarType, WithMessageBarColors> = {
    informative: { $colorSubtle: colors.backgroundInfoSubtle, $color: colors.borderInfo },
    success: { $colorSubtle: colors.backgroundSuccessSubtle, $color: colors.borderSuccess },
    error: { $colorSubtle: colors.backgroundErrorSubtle, $color: colors.borderError },
    warning: { $colorSubtle: colors.backgroundWarningSubtle, $color: colors.borderWarning },
  };
  return colorMap[messageBarType];
}

interface WithMessageBarColors {
  $color: string;
  $colorSubtle: string;
}
const StyledMessageBar = styled.div<WithMessageBarColors>`
  display: flex;
  align-items: center;
  padding: 12px;
  background: ${({ $colorSubtle }) => $colorSubtle};
  border-left: 6px solid ${({ $color }) => $color};
`;

const StyledIconSpan = styled.span<WithMessageBarColors>`
  width: 24px;
  height: 24px;
  margin-right: 8px;
  color: ${({ $color }) => $color};
  svg {
    width: 24px;
    height: 24px;
  }
`;

const MessageSpan = styled.span`
  margin-right: 8px;
  white-space: pre-wrap;
`;
